// Pure disclosure math for the marketplace (spec §7.3). Splits a manifest's
// permissions into the mandatory "Requires" group and the optional "May request
// later" group, computes the one-glance access badge over ALL declared caps
// (reusing Part 3's computeAccessBadge), and diffs new mandatory permissions for
// the update re-consent gate (spec §9.4). No React, no DOM: trivially testable.

import type { PluginManifest, PermissionRequest } from '@slate/plugin-api';
import { computeAccessBadge, type AccessBadge } from '../plugins/accessBadge';

export interface DisclosureLine {
  capability: PermissionRequest['capability'];
  scope?: string;
  domains?: string[];
  reason: string;
}

export interface Disclosure {
  requires: DisclosureLine[];
  optional: DisclosureLine[];
  badge: AccessBadge;
}

/** Drop undefined optional fields so test equality and rendering stay clean. */
function toLine(p: PermissionRequest): DisclosureLine {
  const line: DisclosureLine = { capability: p.capability, reason: p.reason };
  if (p.scope !== undefined) line.scope = p.scope;
  if (p.domains !== undefined) line.domains = p.domains;
  return line;
}

export function computeDisclosure(manifest: PluginManifest): Disclosure {
  const perms = manifest.permissions ?? [];
  const requires = perms.filter((p) => !p.optional).map(toLine);
  const optional = perms.filter((p) => p.optional).map(toLine);
  // Badge over the maximum potential access (mandatory + optional). Nothing is
  // granted yet; this is the pre-install blast-radius glance.
  const badge = computeAccessBadge(perms.map((p) => p.capability));
  return { requires, optional, badge };
}

export function newMandatoryPermissions(
  prev: PermissionRequest[],
  next: PermissionRequest[],
): PermissionRequest[] {
  const wasMandatory = new Set(prev.filter((p) => !p.optional).map((p) => p.capability));
  return next.filter((p) => !p.optional && !wasMandatory.has(p.capability));
}
